// BITÁCORA OPERATIVA V3 — memoria operativa navegable de Galarya.
// Una sola corriente cronológica que une CASOS/ALARMAS (nuevas · en atención · cerradas) y EVENTOS.
// Relación evento↔caso: AUTORITATIVA (evento.alarma_id). Los eventos que ya generaron un caso se
// representan por su caso (no se duplican); los eventos sin alarma aparecen como registros propios.
// Los casos cerrados NUNCA desaparecen. Reutiliza los mappers/copy de Central V3 y CaseDetailV3.
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail, getAlarmas, textoListaIncompleta } from "@/lib/api";
import { toast } from "sonner";
import { useScenario } from "@/context/ScenarioContext";
import ScenarioSwitch from "@/components/ScenarioSwitch";
import CaseDetailV3 from "@/components/consolaV3/CaseDetailV3";
import { verboEvento, iconoEvento, esTecnica } from "@/lib/consolaV3/meta";
import ClipEvento from "@/components/eventos/ClipEvento";
import { lecturaEkhEvento } from "@/lib/consolaV3/ekhEvento";
import { prioridadMeta } from "@/lib/alarmMeta";
import { fmtTimestamp, fmtTimestampSeg } from "@/lib/format";
import { ScrollText, X, Wrench, MapPin, Clock, Radio, ChevronRight, Inbox, Search } from "lucide-react";
import { ExportarBitacora } from "@/components/reportes/ReporteControles";

const useNow = () => { const [n, s] = useState(Date.now()); useEffect(() => { const t = setInterval(() => s(Date.now()), 1000); return () => clearInterval(t); }, []); return n; };

// BIT-01 — comparación tolerante a acentos y mayúsculas para la búsqueda por nombre.
const norm = (s) => (s ?? "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const ESTADO_CASO = {
  recibida: { label: "Nueva", cls: "bg-[#FE6A55]/15 text-[#C2410C]" },
  tomada: { label: "En atención", cls: "bg-amber-100 text-amber-800" },
  en_verificacion: { label: "En verificación", cls: "bg-amber-100 text-amber-800" },
  no_verificable: { label: "En atención", cls: "bg-amber-100 text-amber-800" },
  escalada: { label: "Escalada", cls: "bg-[#FE6A55]/20 text-[#FE6A55]" },
  cerrada: { label: "Cerrada", cls: "bg-emerald-100 text-emerald-700" },
};

const PRIORIDADES = ["alta", "media", "baja"];

function EventoDetalle({ ev, onOpenCaso, demo = false }) {
  const Icon = iconoEvento(ev.tipo_evento);
  const esAudio = ev.tipo_evento === "AUDIO_SESSION_STARTED" || ev.tipo_evento === "AUDIO_SESSION_ENDED";
  // NAV-003: hecho operativo registrado FUERA de un caso (proyección de lectura; no se reescribe).
  const esContacto = ev.tipo_evento === "CONTACTO_OPERATIVO";
  const tieneCaso = !!ev.alarma_id;
  return (
    <div data-testid="bitacora-evento-detalle" className="space-y-3.5">
      <div className="rounded-xl border-2 border-slate-200 bg-slate-50 p-5">
        <span data-testid="bitacora-evento-badge" className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${tieneCaso ? "bg-[#01CBE3]/20 text-[#0891A6]" : "bg-slate-200 text-slate-600"}`}>
          {tieneCaso ? (esAudio ? "Sesión de audio · asociada a caso" : "Evento · asociado a caso")
            : esContacto ? "Hecho operativo · sin caso" : "Evento · sin alarma"}
        </span>
        <div className="mt-2 flex items-start gap-3">
          <Icon strokeWidth={1.9} className="h-8 w-8 shrink-0 text-slate-500" />
          <div className="min-w-0">
            <h1 className="text-2xl font-extrabold tracking-tight text-[#012472] break-words">{verboEvento(ev.tipo_evento)}</h1>
            {ev.persona_nombre && <p className="mt-1 text-lg font-bold text-slate-800 break-words">{ev.persona_nombre}</p>}
          </div>
        </div>
        {ev.ekh_lectura && <p className="mt-3 rounded-lg bg-[#012472]/[0.05] px-3 py-2 text-sm font-semibold text-[#012472]">{ev.ekh_lectura}</p>}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2 text-sm">
        {(ev.sitio_nombre || ev.zona_nombre) && <p className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4 text-slate-400" /> {[ev.sitio_nombre, ev.zona_nombre].filter(Boolean).join(" · ")}</p>}
        <p className="flex items-center gap-2 text-slate-600"><Clock className="h-4 w-4 text-slate-400" /> Recibido {fmtTimestamp(ev.hora_recepcion_servidor)}</p>
        {ev.timestamp_dispositivo && <p className="flex items-center gap-2 text-slate-500"><Radio className="h-4 w-4 text-slate-400" /> Dispositivo {fmtTimestamp(ev.timestamp_dispositivo)}</p>}
        {ev.origen && <p className="text-xs uppercase tracking-wide text-slate-400">Origen: {ev.origen}</p>}
        {esContacto && (
          <div data-testid="bitacora-contacto-operativo" className="pt-1 space-y-1.5">
            {ev.quien && <p className="text-slate-700"><span className="font-semibold">Contacto:</span> {ev.quien}{ev.relacion ? ` · ${ev.relacion}` : ""}</p>}
            {ev.motivo && <p className="text-slate-700"><span className="font-semibold">Motivo:</span> {ev.motivo}</p>}
            {ev.nota && <p className="text-slate-700"><span className="font-semibold">Qué se informó:</span> {ev.nota}</p>}
            {ev.operador && <p className="text-slate-600"><span className="font-semibold">Registró:</span> {ev.operador}</p>}
            <p className="text-xs text-slate-400">Hecho operativo registrado fuera de una alarma. No generó caso.</p>
          </div>
        )}
        {tieneCaso ? (
          <div className="pt-1 space-y-2">
            {onOpenCaso && <button data-testid="bitacora-evento-ver-caso" onClick={() => onOpenCaso(ev.alarma_id)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#012472]/5 px-3 py-1.5 text-xs font-semibold text-[#012472] hover:bg-[#012472]/10 transition-colors">
              Ver caso asociado <ChevronRight className="h-3.5 w-3.5" />
            </button>}
            <p className="text-xs text-slate-400">{esAudio ? "Sesión de audio asociada al caso. Sólo se traza inicio/fin — no se almacena contenido de audio." : "Este evento está asociado a un caso."}</p>
          </div>
        ) : (
          <p className="pt-1 text-xs text-slate-400">{esContacto ? "Se muestra con su información autoritativa disponible." : "Este evento no generó alarma. Se muestra sólo con su información autoritativa disponible."}</p>
        )}
      </div>
      {/* CLIPS-HM-01 — un evento INFORMATIVO (sin caso) también puede tener su clip. */}
      <ClipEvento eventoId={ev.id} demo={demo} />
    </div>
  );
}

export default function Bitacora() {
  const nowMs = useNow();
  const { scenario, demo, isDemo } = useScenario();
  const [sp, setSp] = useSearchParams();
  const personaId = sp.get("persona_id");
  // NAV-001: la Bitácora puede recibir el id técnico de un caso para conservar el contexto al
  // llegar desde otra pantalla (sin PII en la URL y sin persistencia nueva).
  const casoId = sp.get("caso_id");
  const [sitios, setSitios] = useState([]);
  const [filtros, setFiltros] = useState({ sitio_id: "", prioridad: "", clase: "" });
  // BIT-01 — texto libre. `busqueda` es lo que se teclea; `termino` es el valor con debounce que
  // viaja al backend (los eventos se buscan en el histórico completo, no en el lote cargado).
  const [busqueda, setBusqueda] = useState("");
  const [termino, setTermino] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setTermino(busqueda.trim()), 300);
    return () => clearTimeout(t);
  }, [busqueda]);
  const [casos, setCasos] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null); // { kind, id } | { kind:'evento', ev }
  const [caseDetail, setCaseDetail] = useState(null);
  const listRef = useRef(null);
  const ticketRef = useRef(0);
  const [listaParcial, setListaParcial] = useState("");  // "" = se recorrieron ambas colas enteras

  useEffect(() => { if (!isDemo) api.get("/sitios").then(({ data }) => setSitios(data)).catch(() => {}); }, [isDemo]);

  const cargar = useCallback(async (silent) => {
    // El turno se toma SIEMPRE, también en DEMO: así una lectura REAL pendiente queda invalidada
    // al cambiar de escenario y no puede pintar datos reales encima del escenario vigente.
    const myTicket = (ticketRef.current += 1);
    if (!silent) setLoading(true);
    if (isDemo) { setCasos(demo.alarmas); setEventos(demo.eventos); setListaParcial(""); setLoading(false); return; }
    try {
      const [abiertas, cerradas, evs] = await Promise.all([
        getAlarmas(),
        getAlarmas({ estado: "cerradas" }),
        api.get("/bitacora-eventos", { params: { limit: 200, ...(personaId ? { persona_id: personaId } : {}), ...(termino ? { busqueda: termino } : {}) } }),
      ]);
      if (myTicket !== ticketRef.current) return;   // respuesta vieja: no pisa una más reciente
      const porId = new Map();
      [...abiertas, ...cerradas].forEach((a) => { if (a?.id && !porId.has(a.id)) porId.set(a.id, a); });
      setCasos([...porId.values()]);
      setListaParcial([textoListaIncompleta(abiertas), textoListaIncompleta(cerradas)]
        .filter(Boolean).join(" ")); 
      setEventos(evs.data.eventos || []);
    } catch (e) { if (myTicket === ticketRef.current) toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { if (myTicket === ticketRef.current) setLoading(false); }
  }, [isDemo, demo, personaId, termino]);

  useEffect(() => { cargar(false); }, [cargar, scenario]);
  useEffect(() => {
    if (isDemo) return;
    const t = setInterval(() => { if (!sel) cargar(true); }, 6000);
    return () => clearInterval(t);
  }, [isDemo, cargar, sel]);

  // Fila unificada: casos (todos, incl. cerrados) + eventos SIN alarma (autoritativo por alarma_id).
  const rows = useMemo(() => {
    const caseRows = casos.map((a) => ({
      key: `c-${a.id}`, kind: "caso", id: a.id, hora: a.hora_recepcion_servidor,
      tipo: a.tipo, prioridad: a.prioridad === "maxima" ? "alta" : a.prioridad, estado: a.estado,
      persona: a.persona_nombre, sitio: a.sitio_nombre, zona: a.zona_nombre, esTecnica: esTecnica(a),
      persona_id: a.persona_id,
      ekh: a.ekh_evento || null,
    }));
    const evRows = eventos.filter((e) => !e.alarma_id || e.tipo_evento === "AUDIO_SESSION_STARTED" || e.tipo_evento === "AUDIO_SESSION_ENDED").map((e) => ({
      key: `e-${e.id}`, kind: "evento", id: e.id, hora: e.hora_recepcion_servidor, ev: e,
      tipo: e.tipo_evento, prioridad: e.prioridad === "maxima" ? "alta" : e.prioridad, estado: "evento",
      persona: e.persona_nombre, sitio: e.sitio_nombre || "", zona: e.zona_nombre, esTecnica: false,
      persona_id: e.persona_id,
      ekhLectura: e.ekh_lectura || null,
    }));
    let all = [...caseRows, ...evRows];
    if (personaId) all = all.filter((r) => r.persona_id === personaId);
    if (filtros.sitio_id) { const nom = sitios.find((s) => s.id === filtros.sitio_id)?.nombre; all = all.filter((r) => r.sitio === nom); }
    if (filtros.prioridad) all = all.filter((r) => r.prioridad === filtros.prioridad);
    if (filtros.clase === "casos") all = all.filter((r) => r.kind === "caso");
    if (filtros.clase === "eventos") all = all.filter((r) => r.kind === "evento");
    // BIT-01 — el mismo término se aplica a los casos (que llegan completos) y a los eventos ya
    // filtrados en servidor: nombre/fragmento, id del caso o id del evento (prefijo o fragmento).
    if (termino) {
      const t = norm(termino);
      all = all.filter((r) => norm(r.persona).includes(t)
        || String(r.id).toLowerCase().includes(t)
        || norm(verboEvento(r.tipo)).includes(t)
        || String(r.ev?.alarma_id || "").toLowerCase().includes(t));
    }
    return all.sort((a, b) => (b.hora || "").localeCompare(a.hora || ""));
  }, [casos, eventos, filtros, sitios, personaId, termino]);

  const abrir = async (row) => {
    if (row.kind === "evento") { setSel({ kind: "evento", ev: row.ev }); setCaseDetail(null); return; }
    setSel({ kind: "caso", id: row.id });
    if (isDemo) { setCaseDetail(demo.detalles[row.id] || null); return; }
    setCaseDetail(null);
    try { const { data } = await api.get(`/alarmas/${row.id}`); setCaseDetail(data); }
    catch (e) { toast.error("No se pudo cargar el detalle del caso."); setSel(null); }
  };
  const abrirCaso = async (id) => {
    if (!id) return;
    setSel({ kind: "caso", id });
    if (isDemo) { setCaseDetail(demo.detalles[id] || null); return; }
    setCaseDetail(null);
    try { const { data } = await api.get(`/alarmas/${id}`); setCaseDetail(data); }
    catch (e) { toast.error("No se pudo cargar el detalle del caso."); setSel(null); }
  };
  const cerrar = () => {
    setSel(null);
    setCaseDetail(null);
    // La URL y la pantalla representan el mismo estado: al cerrar el caso deja de viajar su id.
    if (casoId) { const n = new URLSearchParams(sp); n.delete("caso_id"); setSp(n, { replace: true }); }
  };

  // NAV-003 / NAV-001: el contexto recibido por URL se REPRESENTA en pantalla y se puede limpiar.
  const nombreContexto = personaId ? (rows.find((r) => r.persona)?.persona || null) : null;
  const limpiarContexto = () => {
    const n = new URLSearchParams(sp);
    n.delete("persona_id");
    n.delete("caso_id");
    setSp(n, { replace: true });
    setSel(null);
    setCaseDetail(null);
  };

  // Caso recibido por URL: se abre una sola vez (no se reabre al refrescar la lista).
  const casoAbiertoRef = useRef(null);
  useEffect(() => {
    if (!casoId || loading) return;
    if (casoAbiertoRef.current === casoId) return;
    casoAbiertoRef.current = casoId;
    abrirCaso(casoId);
  }, [casoId, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const hayVacio = !loading && rows.length === 0;

  return (
    <div data-testid="bitacora-page" className="tema-noche-auto pb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <ScrollText strokeWidth={1.7} className="h-7 w-7 text-[#012472]" />
          <div>
            <h1 className="text-2xl font-semibold text-[#012472]">Bitácora operativa</h1>
            <p className="text-sm text-slate-500">Memoria navegable: eventos y casos (nuevos · en atención · cerrados). Un caso no desaparece al cerrarse.</p>
          </div>
        </div>
        <ScenarioSwitch />
      </div>

      {/* Contexto recibido por URL (persona o caso): visible y reversible (NAV-001/NAV-003). */}
      {!isDemo && listaParcial && (
        <div data-testid="bitacora-lista-incompleta"
          className="mb-4 rounded-xl border border-amber-400 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-900">
          Lista incompleta — {listaParcial}
        </div>
      )}
      {(personaId || casoId) && (
        <div data-testid="bitacora-contexto"
          className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-[#01CBE3]/40 bg-[#01CBE3]/5 px-4 py-2.5">
          <p className="text-sm text-[#012472]">
            {personaId
              ? <>Historial de <span className="font-semibold">{nombreContexto || "una persona"}</span>: sólo sus casos y eventos.</>
              : <>Caso en contexto: <span className="font-mono text-xs font-semibold">{casoId.slice(0, 8)}</span>.</>}
          </p>
          <button data-testid="bitacora-contexto-limpiar" onClick={limpiarContexto}
            className="rounded-lg border border-[#012472]/20 px-2.5 py-1 text-xs font-semibold text-[#012472] hover:bg-[#012472]/5 transition-colors">
            Ver bitácora completa
          </button>
        </div>
      )}

      {/* Filtros. Cada control lleva etiqueta visible + nombre accesible (A11Y-005). */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        {!isDemo && (
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
            Sitio
            <select data-testid="bitacora-filtro-sitio" value={filtros.sitio_id} aria-label="Filtrar la bitácora por sitio"
              onChange={(e) => setFiltros((f) => ({ ...f, sitio_id: e.target.value }))}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
              <option value="">Todos los sitios</option>
              {sitios.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
          </label>
        )}
        {/* BIT-01 — búsqueda de texto libre: debajo de Sitio y antes de Tipo de registro. */}
        <label className="flex w-full flex-col gap-1 text-xs font-medium text-slate-500 sm:w-72">
          Buscar
          <span className="relative block">
            <Search strokeWidth={1.8} className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input data-testid="bitacora-buscar" type="text" value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar persona, caso o evento"
              aria-label="Buscar en la bitácora por persona, caso o evento"
              className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-8 text-sm text-slate-700 placeholder:text-slate-400 focus:border-[#01CBE3] focus:outline-none transition-colors" />
            {busqueda && (
              <button data-testid="bitacora-buscar-limpiar" onClick={() => setBusqueda("")}
                aria-label="Limpiar la búsqueda"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors">
                <X className="h-4 w-4" />
              </button>
            )}
          </span>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
          Tipo de registro
          <select data-testid="bitacora-filtro-clase" value={filtros.clase} aria-label="Filtrar por tipo de registro"
            onChange={(e) => setFiltros((f) => ({ ...f, clase: e.target.value }))}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
            <option value="">Casos y eventos</option>
            <option value="casos">Sólo casos</option>
            <option value="eventos">Sólo eventos</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
          Prioridad
          <select data-testid="bitacora-filtro-prioridad" value={filtros.prioridad} aria-label="Filtrar la bitácora por prioridad"
            onChange={(e) => setFiltros((f) => ({ ...f, prioridad: e.target.value }))}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
            <option value="">Toda prioridad</option>
            {PRIORIDADES.map((p) => <option key={p} value={p}>{prioridadMeta(p).label}</option>)}
          </select>
        </label>
      </div>

      {/* REPORT-01 — exportación por SITIO y periodo. No exporta «lo que se ve»: consulta todos los
          registros elegibles del periodo en el servidor. Los filtros de pantalla (persona,
          prioridad, tipo de registro) se trasladan al reporte tal como están aplicados. */}
      <ExportarBitacora sitioId={filtros.sitio_id} demo={isDemo}
        sitioNombre={sitios.find((s) => s.id === filtros.sitio_id)?.nombre}
        personaId={personaId} prioridad={filtros.prioridad} clase={filtros.clase} />

      <div ref={listRef} className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
        {loading ? (
          <p className="p-6 text-sm text-slate-400">Cargando bitácora…</p>
        ) : hayVacio ? (
          <div className="p-12 text-center">
            <Inbox strokeWidth={1.3} className="h-9 w-9 mx-auto text-slate-300" />
            <p data-testid="bitacora-vacio" className="mt-2 text-sm text-slate-500">
              {termino
                ? <>No hay casos ni eventos que coincidan con «{termino}»{(filtros.sitio_id || filtros.clase || filtros.prioridad) ? " y los filtros aplicados" : ""}.</>
                : personaId ? "No hay casos ni eventos de esta persona con estos filtros." : "No hay registros con estos filtros."}
            </p>
            {personaId && (
              <p className="mt-1 text-xs text-slate-400">
                La Bitácora reúne casos y eventos. Las gestiones registradas dentro de un caso
                (llamadas, notas, pasos) se consultan en el caso, no como registro propio.
              </p>
            )}
          </div>
        ) : (
          rows.map((r) => {
            const Icon = iconoEvento(r.tipo);
            const pm = prioridadMeta(r.prioridad);
            const est = r.kind === "evento" ? { label: "Evento", cls: "bg-slate-100 text-slate-500" } : (ESTADO_CASO[r.estado] || { label: r.estado, cls: "bg-slate-100 text-slate-600" });
            return (
              <button key={r.key} data-testid={`bitacora-row-${r.id}`} onClick={() => abrir(r)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${r.esTecnica ? "bg-[#9B2C2C]/10 text-[#9B2C2C]" : "bg-[#012472]/5 text-[#012472]"}`}>
                  {r.esTecnica ? <Wrench className="h-5 w-5" strokeWidth={1.7} /> : <Icon className="h-5 w-5" strokeWidth={1.7} />}
                </span>
                <div className="min-w-0 flex-1">
                  {/* v2.13.3: los verbos largos (p. ej. RECORDATORIO PROGRAMADO + nombre completo)
                      se cortaban con la elipsis y el registro quedaba ilegible. Ahora envuelven. */}
                  <p className="text-sm font-semibold text-slate-800 break-words">
                    {verboEvento(r.tipo)}
                    {r.persona ? <span className="font-normal text-slate-500"> · {r.persona}</span> : null}
                  </p>
                  <p className="text-xs text-slate-400 break-words">
                    {[r.sitio, r.zona].filter(Boolean).join(" · ")}
                    {(r.sitio || r.zona) ? " · " : ""}<span className="tabular-nums">{fmtTimestampSeg(r.hora)}</span>
                    <span className="ml-1.5 rounded bg-slate-100 px-1 py-0.5 text-[10px] font-mono text-slate-500">#{String(r.id).slice(0, 8)}</span>
                    <span className="ml-1.5 hidden rounded bg-slate-100 px-1 py-0.5 text-[10px] font-mono text-slate-400 lg:inline">{r.tipo}</span>
                    {(r.ekh || r.ekhLectura) && (() => {
                      const ekh = r.ekh ? lecturaEkhEvento({ tipo: r.tipo, ekh_evento: r.ekh }) : null;
                      const txt = r.ekhLectura || (ekh ? (ekh.presente ? `${ekh.texto} al momento del evento` : ekh.texto) : "");
                      return txt ? <span data-testid={`bitacora-ekh-${r.id}`} className={`ml-1.5 text-[11px] font-semibold ${ekh && !ekh.presente ? "text-slate-500" : "text-[#012472]"}`}>{txt}</span> : null;
                    })()}
                  </p>
                </div>
                {r.kind === "caso" && r.prioridad && (
                  <span className={`hidden sm:inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${pm.badge}`}>
                    <pm.Icon className="h-3 w-3" strokeWidth={2} /> {pm.label}
                  </span>
                )}
                <span data-testid={`bitacora-estado-${r.id}`} className={`shrink-0 rounded-md px-2 py-0.5 text-[11px] font-bold ${est.cls}`}>{est.label}</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
              </button>
            );
          })
        )}
      </div>

      {/* Drawer de detalle — EN PORTAL AL BODY. Causa del glitch: el wrapper `.galarya-fade` del
          shell anima `transform` con `both`, así que conserva el transform final y se convierte en
          containing block de `position: fixed`; con la lista scrolleada el panel se dibujaba miles
          de px arriba del viewport. Mismo panel, read-only y filtros intactos. */}
      {sel && createPortal(
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="bitacora-drawer">
          {/* CAUSA de la pantalla negra en NOCHE: la marca del puente estaba en ESTE contenedor, que
              cubre `inset-0`, y la regla NIVEL 0 (`.dark .tema-noche-auto {background:#08111C}`) lo
              pintaba OPACO sobre toda la ventana: la Bitácora no quedaba atenuada, quedaba tapada.
              En claro no hay tal regla, de ahí que sólo fallara de noche. La marca se mueve al PANEL
              (sigue traduciendo el tema de todo su contenido) y el velo vuelve a ser el único
              atenuador. De noche el fondo ya es casi negro, así que el velo se reduce y se apoya en
              un desenfoque leve: el drawer sigue siendo el foco sin apagar la aplicación. */}
          <div className="absolute inset-0 bg-black/40 dark:bg-black/25 dark:backdrop-blur-[2px]" onClick={cerrar} />
          <div className="tema-noche-auto relative z-10 h-full w-full max-w-[560px] overflow-y-auto border-l border-transparent bg-[#F1F5F9] shadow-2xl dark:border-[#263B5D]">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
              <p className="text-sm font-bold text-[#012472]">{sel.kind === "evento" ? "Detalle del evento" : "Detalle del caso"}</p>
              <button data-testid="bitacora-drawer-close" onClick={cerrar} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4">
              {sel.kind === "evento" ? (
                <EventoDetalle ev={sel.ev} onOpenCaso={abrirCaso} demo={isDemo} />
              ) : caseDetail ? (
                <CaseDetailV3 detail={caseDetail} nowMs={nowMs} busy={false} demo={isDemo} readOnly
                  permitirReporteIncidente
                  onTake={() => {}} onCloseClick={() => {}} onLiberar={() => {}}
                  onSolicitarApoyo={() => {}} onTransition={() => {}} onAtenderTambien={() => {}}
                  takeBlockedAlta={false} />
              ) : (
                <p className="p-6 text-sm text-slate-400">Cargando detalle…</p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
